import { LitElement, html, css, unsafeCSS, property } from "lit-element";
import CardComponentType from "../../enums/card-component-type";
import HighlightRule from "../../enums/highlight-rule";
import MetricCalculationConfigType from "../../enums/metric-calculation-config-type";
import MetricValueCalculationType from "../../enums/metric-value-calculation-type";
import { DropdownModel } from "../../models/components/DropdownModel";
import { ReportConfigModel } from "../../models/report/ReportConfigModel";
import { ColumnModel } from "../../models/template/ColumnModel";
import { ComponentMetricValueModel } from "../../models/template/ComponentMetricValueModel";
import { ComponentModel } from "../../models/template/ComponentModel";
import { RowModel } from "../../models/template/RowModel";
import { TemplateModel } from "../../models/template/TemplateModel";
import Dropdown from "../dropdown/dropdown";
import { v4 as uuid } from 'uuid';

import reportTemplateViewStyles from './report-template-view.scss';
import { ComponentTableHeaders } from "../../models/template/ComponentTableHeaders";
import Consts from "../../consts/consts";
import ReportType from "../../enums/report-type";
import MetricConditionalFormatType from "../../enums/metric-conditional-format-type";
import MetricConditionalBoundType from "../../enums/metric-conditional-bound-type";

export default class ReportTemplateView extends LitElement {
    @property({ type: Object })
    reportConfig: ReportConfigModel;

    @property({ type: Object })
    reportTemplate: TemplateModel;

    static get styles() {
        return css`${unsafeCSS(reportTemplateViewStyles)}`;
    }

    constructor(reportTemplate: TemplateModel, reportConfig: ReportConfigModel) {
        super();

        this.reportTemplate = reportTemplate;
        this.reportConfig = reportConfig;
    }

    valueChange(value, item, key, index = undefined) {
        if (index != undefined) {
            value[index][key] = value;
        } else {
            item[key] = value;
        }

        console.log(item, key, value, index);
    }

    valueNumberChange(value, item, key, index = undefined) {
        if (index != undefined) {
            value[index][key] = parseInt(value);
        } else {
            item[key] = parseInt(value);
        }

        console.log(item, key, value, index);
    }

    sortItem(items, item, sortUp: boolean) {

        if (sortUp) {

            let index = items.indexOf(item);
            if (items[index + 1]) {
                items[index + 1].order--;
                item.order++;

                this.requestUpdate();
            }

        } else {

            let index = items.indexOf(item);
            if (items[index - 1]) {
                items[index - 1].order++;
                item.order--;

                this.requestUpdate();
            }

        }

    }

    buildRows(rows: RowModel[]) {
        let htmlRows = [];

        for (let [i, row] of Object.entries(rows.sort(this.sortOrder))) {
            let rowId = uuid();

            htmlRows.push(html`
                <div class="row">
                    <div class="col-auto align-self-end">
                        <button
                            data-toggle="collapse"
                            data-target="#row-${rowId}"
                            class="btn btn-warning"
                            @click=${(event) => this.toggleIcon(event.target, ` Row ${row["id"]}`)}>
                                <i class="fa fa-chevron-down"></i>
                                Row ${row["id"]}
                        </button>
                    </div> 
                    
                    <div class="col-auto align-self-end">
                        <button class="btn btn-info" @click=${(event) => this.sortItem(rows, row, false)} >
                            <i class="fa fa-chevron-up"></i>
                        </button>
                    </div>

                    <div class="col-auto align-self-end">
                        <button class="btn btn-info" @click=${(event) => this.sortItem(rows, row, true)} >
                            <i class="fa fa-chevron-down"></i>
                        </button>
                    </div>
                </div>
                <div class="collapse indent" id="row-${rowId}">
                    <div class="row">                          
                        <div class="col-auto align-self-end">
                            <button data-toggle="collapse"
                                data-target="#column-${rowId}"
                                class="btn btn-warning"
                                @click=${(event) => this.toggleIcon(event.target, " Column")}>
                                    <i class="fa fa-chevron-down"></i>
                                    Column
                            </button>
                        </div>

                        <div class="col-auto align-self-end">
                            <button @click=${() => this.removeRow(rows, row)} class="btn btn-danger">
                                <i class="fa fa-trash"></i>
                            </button>
                        </div>

                        <div class="col-auto align-self-end">
                            <button @click=${() => this.addColumn(row.columns)} class="btn btn-success">Add Column</button>
                        </div>
                    </div>

                    <div class="collapse indent" id="column-${rowId}">
                        ${this.buildColumns(row.columns)}                        
                    </div>
                </div>
                `);
        }

        return htmlRows;
    }

    removeRow(rows: RowModel[], row: RowModel) {
        let rowIndex = rows.indexOf(row);

        rows.splice(rowIndex, 1);

        this.requestUpdate();
    }

    removeComponent(components: ComponentModel[], component: ComponentModel) {
        let componentIndex = components.indexOf(component);

        components.splice(componentIndex, 1);

        this.requestUpdate();
    }

    buildColumns(columns: ColumnModel[]) {
        let htmlColumns = [];

        for (let [i, column] of Object.entries(columns.sort(this.sortOrder))) {
            let hasRows = (column.rows && column.rows.length);
            let columnId = uuid();
            let expandLabel = hasRows ? " Rows" : " Components";

            htmlColumns.push(html`   
                <div class="row">                     
                    <div class="col-auto align-self-end">
                        <button class="btn btn-warning"
                            data-target="#row-components-${columnId}"
                            data-toggle="collapse"
                            @click=${(event) => this.toggleIcon(event.target, expandLabel)}>
                                <i class="fa fa-chevron-down"></i>
                                ${expandLabel}
                        </button>
                    </div>
                
                    <div class="col">
                        <label>Column Heading</label>                      
                        <input type="text"
                            class="form-control"
                            @change=${(event) => this.valueChange(event.target.value, column, "heading")} 
                            value=${column.heading} />
                    </div>  
                    
                    <div class="col-auto align-self-end">
                        <button class="btn btn-info" @click=${(event) => this.sortItem(columns, column, false)} >
                            <i class="fa fa-chevron-up"></i>
                        </button>
                    </div>

                    <div class="col-auto align-self-end">
                        <button class="btn btn-info" @click=${(event) => this.sortItem(columns, column, true)} >
                            <i class="fa fa-chevron-down"></i>
                        </button>
                    </div>

                    <div class="col-auto align-self-end">                        
                        <button @click=${() => this.addRow(column.rows)} class="btn btn-success">Add Row</button>
                        <button @click=${() => this.addComponent(column.components)} class="btn btn-success">Add Component</button>
                    </div>
                </div>
                
                <div class="collapse indent" id="row-components-${columnId}">
                    ${(hasRows ? this.buildRows(column.rows) : this.buildComponents(column.components))}
                </div>
                `);
        }

        return htmlColumns;
    }

    addColumn(columns: ColumnModel[]) {
        columns.sort(this.sortOrder);

        let columnsLength = columns.length;
        let lastColumn = columns[columnsLength - 1];

        columns.push(<ColumnModel>{
            order: (lastColumn ? lastColumn.order + 1 : 0),
            heading: "",
            components: [],
            rows: []
        });

        this.requestUpdate();
    }

    addRow(rows: RowModel[]) {
        rows.sort(this.sortOrder);

        let rowsLength = rows.length;
        let lastRow = rows[rowsLength - 1];

        rows.push(<RowModel>{
            order: (lastRow ? lastRow.order + 1 : 0),
            columns: []
        });

        this.requestUpdate();
    }

    addComponent(components: ComponentModel[]) {
        components.sort(this.sortOrder);

        let componentsLength = components.length;
        let lastComponent = components[componentsLength - 1];

        components.push(<ComponentModel>{
            heading: "",
            tableHeaders: [],
            order: (lastComponent ? lastComponent.order + 1 : 0),
            highlightRule: 0,
            type: 0,
            homeTeamMetricValue: <ComponentMetricValueModel>{
                order: 0,
                totalMetricId: Consts.EmptyGuid,
                type: 0,
                valueMetricId: Consts.EmptyGuid,
            },
            awayTeamMetricValue: <ComponentMetricValueModel>{
                order: 0,
                totalMetricId: Consts.EmptyGuid,
                type: 0,
                valueMetricId: Consts.EmptyGuid,
            },
            playerMetricValues: [],
            teamMetricValues: []
        });

        this.requestUpdate();
    }

    sortOrder(a, b) {
        return a.order - b.order;
    }

    sortName(a, b) {
        return a.name - b.name;
    }

    sortLabel(a, b) {
        if (a.label < b.label) {
            return -1;
        }

        if (a.label > b.label) {
            return 1;
        }

        return 0;
    }

    toggleIcon(control, text = null) {
        if (control.tagName.toLowerCase() == 'i') {
            control = control.parentNode;
        }

        if (control.classList.contains('expanded')) {
            control.classList.remove('expanded');
            control.innerHTML = '<i class="fa fa-chevron-down"></i>';
        } else {
            control.classList.add('expanded');
            control.innerHTML = '<i class="fa fa-chevron-up"></i>';
        }

        if (text)
            control.innerHTML += text;
    }

    buildComponents(components: ComponentModel[]) {
        let htmlComponents = [];

        for (let [i, component] of Object.entries(components.sort(this.sortOrder))) {
            let componentConfigId = uuid();

            htmlComponents.push(html`
                <div class="row">
                    <div class="col-auto align-self-end"> 
                        <button class="btn btn-warning" 
                            data-toggle="collapse"
                            data-target="#component-config-${componentConfigId}"
                            @click=${(event) => this.toggleIcon(event.target)}>
                                <i class="fa fa-chevron-down"></i>    
                        </button>
                    </div>

                    <div class="col">
                        <label>Component Heading</label>
                        <input type="text"
                            class="form-control"
                            @change=${(event) => this.valueChange(event.target.value, component, "heading")} 
                            value=${component.heading} />
                    </div>

                    <div class="col-auto align-self-end">
                        <button @click=${() => this.removeComponent(components, component)} class="btn btn-danger">
                            <i class="fa fa-trash"></i>
                        </button>
                    </div>

                    <div class="col-auto align-self-end">
                        <button class="btn btn-info" @click=${(event) => this.sortItem(components, component, false)}>
                            <i class="fa fa-chevron-up"></i>
                        </button>
                    </div>

                    <div class="col-auto align-self-end">
                        <button class="btn btn-info" @click=${(event) => this.sortItem(components, component, true)}>
                            <i class="fa fa-chevron-down"></i>
                        </button>
                    </div>
                </div>

                <div class="collapse indent" id="component-config-${componentConfigId}">
                    <div class="col">
                        ${this.buildTypeDropdown(component)}
                    </div>
                    <div class="col">
                        ${this.buildHighlightRuleDropdown(component)}
                    </div>

                    <div class="col">
                        <input type="checkbox" 
                            ?checked=${component.showStartingNumber}
                            @change=${(event) => this.valueChange(event.target.checked, component, "showStartingNumber")} />
                        <label>Show Player Starting Number</label>
                    </div>

                    <div class="col">
                    <input type="checkbox" 
                        ?checked=${component.showFullMetricName}
                        @change=${(event) => this.valueChange(event.target.checked, component, "showFullMetricName")} />
                    <label>Show Full Names on Headings</label>
                    </div>

                    <div class="col">
                    <input type="checkbox" 
                        ?checked=${component.showExportToCSV}
                        @change=${(event) => this.valueChange(event.target.checked, component, "showExportToCSV")} />
                    <label>Show Export to CSV Button</label>
                    </div>

                    <div class="col">
                        <label>Top Player Count</label>
                        <input type="number" 
                            max="100" 
                            min="1" 
                            step="1" 
                            class="form-control" 
                            value="${component.topEntityAmount}"
                            @change=${(event) => this.valueChange(parseInt(event.target.value), component, "topEntityAmount")} />
                    </div>

                    ${(component.type == CardComponentType.PlayerBreakdownTable ?
                    html`${component.playerMetricValues.sort(this.sortOrder).map((playerMetricValue) => this.buildPlayerMetricValue(component.playerMetricValues, playerMetricValue))}
                    <div class="row">
                        <div class="col-auto align-self-end">
                            <button class="btn btn-info" @click=${(event) => this.addPlayerMetricValue(component.playerMetricValues)} >add player metric value</button>
                        </div>
                    </div>
                        ${component.tableHeaders.sort(this.sortOrder).map((tableHeader) => this.buildTableHeader(component.tableHeaders, tableHeader))}
                        <div class="row">
                            <div class="col-auto align-self-end">
                                <button class="btn btn-info" @click=${(event) => this.addTableHeader(component.tableHeaders)} >add table header</button>
                            </div>
                        </div>`
                    : '')}

                    ${(component.type == CardComponentType.TeamBreakdownTable ?
                    html`${component.teamMetricValues.sort(this.sortOrder).map((teamMetricValue) => this.buildPlayerMetricValue(component.teamMetricValues, teamMetricValue))}
                            <div class="row">
                                <div class="col-auto align-self-end">
                                    <button class="btn btn-info" @click=${(event) => this.addPlayerMetricValue(component.teamMetricValues)} >add team metric value</button>
                                </div>
                            </div>
                                ${component.tableHeaders.sort(this.sortOrder).map((tableHeader) => this.buildTableHeader(component.tableHeaders, tableHeader))}
                                <div class="row">
                                    <div class="col-auto align-self-end">
                                        <button class="btn btn-info" @click=${(event) => this.addTableHeader(component.tableHeaders)} >add table header</button>
                                    </div>
                                </div>`
                    : '')}

                    ${this.getTeamConfiguration(component)}
                </div>
            `);
        }

        return htmlComponents;
    }

    getTeamConfiguration(component: ComponentModel) {
        if (component.homeTeamMetricValue && component.awayTeamMetricValue) {
            return html`<div class="col">
                <div class="home-team-config">
                    <label>Home Team Configuration</label>
                    ${this.buildTeamConfig(component, 'homeTeamMetricValue')}
                </div>
            </div>
            <div class="col">
                <div class="away-team-config">
                    <label>Away Team Configuration</label>                        
                    ${this.buildTeamConfig(component, 'awayTeamMetricValue')}
                </div>
            </div>`;
        } else
            return html``;
    }

    removeTableHeader(tableHeaders: ComponentTableHeaders[], tableHeader: ComponentTableHeaders) {
        let tableHeaderIndex = tableHeaders.indexOf(tableHeader);

        tableHeaders.splice(tableHeaderIndex, 1);

        this.requestUpdate();
    }

    addTableHeader(tableHeaders: ComponentTableHeaders[]) {
        tableHeaders.sort(this.sortOrder);

        let tableHeadersLength = tableHeaders.length;
        let lastTableHeader = tableHeaders[tableHeadersLength - 1];

        tableHeaders.push(<ComponentTableHeaders>{
            order: (lastTableHeader ? lastTableHeader.order + 1 : 0),
            fullName: '',
            abbreviation: ''
        });

        this.requestUpdate();
    }

    removePlayerMetricValue(playerMetricValues: ComponentMetricValueModel[], playerMetricValue: ComponentMetricValueModel) {
        let playerMetricValueIndex = playerMetricValues.indexOf(playerMetricValue);

        playerMetricValues.splice(playerMetricValueIndex, 1);

        this.requestUpdate();
    }

    addPlayerMetricValue(playerMetricValues: ComponentMetricValueModel[]) {
        playerMetricValues.sort(this.sortOrder);

        let playerMetricValuesLength = playerMetricValues.length;
        let lastPlayerMetricValue = playerMetricValues[playerMetricValuesLength - 1];

        playerMetricValues.push(<ComponentMetricValueModel>{
            order: (lastPlayerMetricValue ? lastPlayerMetricValue.order + 1 : 0),
            type: 0,
            valueMetricId: '',
            totalMetricId: '00000000-0000-0000-0000-000000000000'
        });

        this.requestUpdate();
    }

    buildTableHeader(tableHeaders: ComponentTableHeaders[], tableHeader: ComponentTableHeaders) {
        return html`<div class="row">
            <div class="col">
                <label>Full name</label>
                <input type="text"
                    class="form-control"
                    value=${tableHeader.fullName}
                    @change=${(event) => this.valueChange(event.target.value, tableHeader, "fullName")}
                    />
            </div>

            <div class="col">
                <label>Abbreviation</label>
                <input type="text"
                    class="form-control"
                    value=${tableHeader.abbreviation}
                    @change=${(event) => this.valueChange(event.target.value, tableHeader, "abbreviation")}
                    />
            </div>

            <div class="col-auto align-self-end">
                <button class="btn btn-danger" @click=${(event) => this.removeTableHeader(tableHeaders, tableHeader)} >
                    <i class="fa fa-trash"></i>
                </button>
            </div>

            <div class="col-auto align-self-end">
                <button class="btn btn-info" @click=${(event) => this.sortItem(tableHeaders, tableHeader, false)} >
                    <i class="fa fa-chevron-up"></i>
                </button>
            </div>

            <div class="col-auto align-self-end">
                <button class="btn btn-info" @click=${(event) => this.sortItem(tableHeaders, tableHeader, true)} >
                    <i class="fa fa-chevron-down"></i>
                </button>
            </div>
        </div>`;
    }

    buildPlayerMetricValue(playerMetricValues: ComponentMetricValueModel[], playerMetricValue: ComponentMetricValueModel) {
        return html`
        <div class="row">

            <div class="col">
                ${this.buildMetricType(playerMetricValue)}
            </div>

            <div class="col">
                ${this.buildMetricDropdown(playerMetricValue, this.valueMetricChange, 'valueMetricId', 'Value Metric')}
            </div>

            <div class="col">
                ${this.buildMetricDropdown(playerMetricValue, this.totalMetricChange, 'totalMetricId', 'Total Metric')}
            </div>

            <div class="col">
            ${this.buildConditionalFormatTypeDropdown(playerMetricValue, this.metricFormatTypeChange, 'formatType', 'Format Rule')}
        </div>

        
        <div class="col">
            <label>Rate Multiplier</label>   
            <input class="form-control" 
            @change=${(event) => this.valueNumberChange(event.target.value, playerMetricValue, "rateValue")} 
            value=${playerMetricValue.rateValue} />
        </div>

            <div class="col-auto align-self-end">
                <button class="btn btn-danger" @click=${(event) => this.removePlayerMetricValue(playerMetricValues, playerMetricValue)} >
                    <i class="fa fa-trash"></i>
                </button>
            </div>

            <div class="col-auto align-self-end">
                <button class="btn btn-info" @click=${(event) => this.sortItem(playerMetricValues, playerMetricValue, false)} >
                    <i class="fa fa-chevron-up"></i>
                </button>
            </div>

            <div class="col-auto align-self-end">
                <button class="btn btn-info" @click=${(event) => this.sortItem(playerMetricValues, playerMetricValue, true)} >
                    <i class="fa fa-chevron-down"></i>
                </button>
            </div>
        </div>
        
        
        <div class="row">

                    
            <div class="col" style="display: none">
                ${this.buildConditionalBoundTypeDropdown(playerMetricValue, this.metricBoundTypeChange, 'boundType', 'Conditional Boundary Type')}
            </div>
                    
            <div class="col" style="display: none">
                <label>Lower Bound Value</label>   
                <input class="form-control" 
                    @change=${(event) => this.valueNumberChange(event.target.value, playerMetricValue, "lowerBoundValue")} 
                    value=${playerMetricValue.lowerBoundValue} />
            </div>

            <div class="col" style="display: none">
                <label>Upper Bound Value</label>   
                <input class="form-control" 
                    @change=${(event) => this.valueNumberChange(event.target.value, playerMetricValue, "upperBoundValue")} 
                    value=${playerMetricValue.upperBoundValue} />
            </div>
            
         </div>`;
    }

    buildTeamConfig(component: ComponentModel, key: string) {
        let team: ComponentMetricValueModel = component[key];

        let htmlTeam = [];

        htmlTeam.push(html`
            ${this.buildMetricType(team)}
            ${this.buildMetricDropdown(team, this.valueMetricChange, 'valueMetricId', 'Value Metric')}
            ${this.buildMetricDropdown(team, this.totalMetricChange, 'totalMetricId', 'Total Metric')}
            `);

        return htmlTeam;
    }

    metricValueTypeChange(value, team: ComponentMetricValueModel) {
        team.type = parseInt(value);
    }

    reportTypeChange(value, template: TemplateModel) {
        template.reportType = parseInt(value);
    }

    typeChange(value, component: ComponentModel) {
        component.type = parseInt(value);
    }

    highlightRuleChange(value, component: ComponentModel) {
        component.highlightRule = parseInt(value);
    }

    valueMetricChange(value, team: ComponentMetricValueModel) {
        team.valueMetricId = value;
    }

    totalMetricChange(value, team: ComponentMetricValueModel) {
        team.totalMetricId = value;
    }

    metricFormatTypeChange(value, team: ComponentMetricValueModel) {
        team.formatType = value;
    }

    metricBoundTypeChange(value, team: ComponentMetricValueModel) {
        team.boundType = value;
    }

    buildMetricDropdown(team: ComponentMetricValueModel, changeFunction, keyValue, label) {
        let dropdownItems: DropdownModel[] = [];

        for (let [i, metric] of Object.entries(this.reportConfig.reportConfigMetrics)) {
            dropdownItems.push(new DropdownModel(metric.metricId, metric.name, team));
        }

        for (let [i, metricCalculation] of Object.entries(this.reportConfig.metricCalculations)) {
            dropdownItems.push(new DropdownModel(metricCalculation.metricId, metricCalculation.name, team));
        }

        for (let [i, metricCalculation] of Object.entries(this.reportConfig.secondMetricCalculations)) {
            dropdownItems.push(new DropdownModel(metricCalculation.metricId, metricCalculation.name, team));
        }

        dropdownItems.sort(this.sortLabel);

        dropdownItems.unshift(new DropdownModel(Consts.EmptyGuid, "Please select a metric", Consts.EmptyGuid));

        return new Dropdown(dropdownItems, changeFunction, team[keyValue], label);
    }

    buildConditionalFormatTypeDropdown(team: ComponentMetricValueModel, changeFunction, keyValue, label) {
        let dropdownItems: DropdownModel[] = [];

        dropdownItems.push(new DropdownModel(MetricConditionalFormatType.None, "None", team));
        dropdownItems.push(new DropdownModel(MetricConditionalFormatType.HigherValueIsBetter, "Higher Value is Better", team));
        dropdownItems.push(new DropdownModel(MetricConditionalFormatType.LowerValueIsBetter, "Lower Value is Better", team));        

        return new Dropdown(dropdownItems, changeFunction, team[keyValue], label);
    }

    buildConditionalBoundTypeDropdown(team: ComponentMetricValueModel, changeFunction, keyValue, label) {
        let dropdownItems: DropdownModel[] = [];

        dropdownItems.push(new DropdownModel(MetricConditionalBoundType.None, "None", team));
        dropdownItems.push(new DropdownModel(MetricConditionalBoundType.PercentageValue, "Percentage Value", team));
        dropdownItems.push(new DropdownModel(MetricConditionalBoundType.FixedValue, "Fixed Value", team));        

        return new Dropdown(dropdownItems, changeFunction, team[keyValue], label);
    }

    buildMetricType(team: ComponentMetricValueModel) {
        let dropdownItems: DropdownModel[] = [];

        dropdownItems.push(new DropdownModel(MetricValueCalculationType.Value, "Value", team));
        dropdownItems.push(new DropdownModel(MetricValueCalculationType.PercentageValue, "Percentage", team));
        dropdownItems.push(new DropdownModel(MetricValueCalculationType.FractionValue, "Fraction", team));
        dropdownItems.push(new DropdownModel(MetricValueCalculationType.PercentageCalculation, "Percentage Calculation", team));
        dropdownItems.push(new DropdownModel(MetricValueCalculationType.Time, "Time", team));
        dropdownItems.push(new DropdownModel(MetricValueCalculationType.Distance, "Distance", team));
        dropdownItems.push(new DropdownModel(MetricValueCalculationType.RateValue, "Rate Value", team));

        return new Dropdown(dropdownItems, this.metricValueTypeChange, team.type.toString(), 'Value Calculation');
    }

    buildHighlightRuleDropdown(component: ComponentModel) {
        let dropdownItems: DropdownModel[] = [];

        dropdownItems.push(new DropdownModel(HighlightRule.None, "None", component));
        dropdownItems.push(new DropdownModel(HighlightRule.AwayTeam, "Away Team", component));
        dropdownItems.push(new DropdownModel(HighlightRule.HomeTeam, "Home Team", component));
        dropdownItems.push(new DropdownModel(HighlightRule.Both, "Both", component));
        dropdownItems.push(new DropdownModel(HighlightRule.Calculate, "Calculate", component));

        return new Dropdown(dropdownItems, this.highlightRuleChange, component.highlightRule.toString(), 'Highlight Rule');
    }

    buildTypeDropdown(component: ComponentModel) {
        let dropdownItems: DropdownModel[] = [];

        dropdownItems.push(new DropdownModel(CardComponentType.HorizontalBarMeterEndStart, "Horizontal Bar Meter End Start", component));
        dropdownItems.push(new DropdownModel(CardComponentType.HorizontalBarMeterMiddleStart, "Horizontal Bar Meter Middle Start", component));
        dropdownItems.push(new DropdownModel(CardComponentType.NumberComparisonTable, "Number Comparison Table", component));
        dropdownItems.push(new DropdownModel(CardComponentType.GaugeChart, "Gauge Chart", component));
        dropdownItems.push(new DropdownModel(CardComponentType.PlayerBreakdownTable, "Player Breakdown Table", component));
        dropdownItems.push(new DropdownModel(CardComponentType.TeamBreakdownTable, "Team Breakdown Table", component));

        return new Dropdown(dropdownItems, this.typeChange, component.type.toString(), 'Component Type');
    }

    buildReportTypeDropdown() {        
        let dropdownItems: DropdownModel[] = [];
        
        dropdownItems.push(new DropdownModel(undefined, "Please select a Report Type", null));
        dropdownItems.push(new DropdownModel(ReportType.TeamMatchReport, "Team Match Report", this.reportTemplate));
        dropdownItems.push(new DropdownModel(ReportType.PlayerMatchReport, "Player Match Report", this.reportTemplate));
        dropdownItems.push(new DropdownModel(ReportType.TeamSeasonReport, "Team Season Report", this.reportTemplate));
        dropdownItems.push(new DropdownModel(ReportType.PlayerSeasonReport, "Player Season Report", this.reportTemplate));
        dropdownItems.push(new DropdownModel(ReportType.TeamSeriesReport, "Team Series Report", this.reportTemplate));
        dropdownItems.push(new DropdownModel(ReportType.PlayerSeriesReport, "Player Series Report", this.reportTemplate));

        return new Dropdown(dropdownItems, this.reportTypeChange, this.reportTemplate.reportType ? this.reportTemplate.reportType.toString() : null, 'Report Type');
    }

    createRenderRoot() {
        return this;
    }

    render() {
        return [
            html` 
            <div class="row">
                <div class="col">
                    <label>Report Template Id</label>           
                    <input type="text"
                        class="form-control"
                        @change=${(event) => this.valueChange(event.target.value, this.reportTemplate, "reportTemplateId")} 
                        value=${this.reportTemplate.reportTemplateId} />
                </div>
            </div>

            <div class="row">
                <div class="col">
                    <label>Name</label>   
                    <input class="form-control" 
                        @change=${(event) => this.valueChange(event.target.value, this.reportTemplate, "name")} 
                        value=${this.reportTemplate.name} />
                </div>
            </div>

            <div class="row">
                <div class="col">
                    <label>Report Id</label>   
                    <input class="form-control" 
                        @change=${(event) => this.valueChange(event.target.value, this.reportTemplate, "reportId")} 
                        value=${this.reportTemplate.reportId} />
                </div>
            </div>

            <div class="row">
                <div class="col">
                    ${this.buildReportTypeDropdown()}
                </div>
            </div>

            <div class="row">
                <div class="col">
                    <label>Base Color</label>   
                    <input type="text"
                        class="form-control"
                        @change=${(event) => this.valueChange(event.target.value, this.reportTemplate, "baseColor")} 
                        value=${this.reportTemplate.baseColor} />
                </div>
            </div>

            <div class="row">
                <div class="col">
                    <label>Secondary Color</label>   
                    <input type="text"
                        class="form-control"
                        @change=${(event) => this.valueChange(event.target.value, this.reportTemplate, "secondaryColor")} 
                        value=${this.reportTemplate.secondaryColor} />
                </div>
            </div>

            <div class="row">
                <div class="col">
                    <label>Away Team Color</label>   
                    <input  type="text"
                        class="form-control"
                        @change=${(event) => this.valueChange(event.target.value, this.reportTemplate.awayTeam, "color")} 
                        value=${this.reportTemplate.awayTeam.color} />
                </div>
            </div>

            <div class="row">
                <div class="col">
                    <label>Home Team Color</label>   
                    <input type="text"
                        class="form-control"
                        @change=${(event) => this.valueChange(event.target.value, this.reportTemplate.homeTeam, "color")} 
                        value=${this.reportTemplate.homeTeam.color} />
                </div>
            </div>

            ${this.buildRows(this.reportTemplate.rows)}

            <div class="row">
                <div class="col">
                    <button @click=${() => this.addRow(this.reportTemplate.rows)} 
                        class="btn btn-success w-100">Add Row</button>            
                </div>
            </div>
            `
        ];
    }
}

window.customElements.define('report-template-view', ReportTemplateView as any);